import { lazy, memo, Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  Download,
  ExternalLink,
  File,
  Grid3X3,
  ImageOff,
  Link2,
  MessageCircle,
  Palette,
  Plus,
  Trash2,
} from "lucide-react";
import type { ChecklistItem, NodeRendererProps, SketchPoint, SketchStroke } from "../../core/editor-types";
import { useEditorStore } from "../../store/editor-store";
import { NodeFrame } from "./NodeFrame";
import { useAssetUrl } from "./useAssetUrl";
import { MarkdownPreview } from "./MarkdownPreview";
import type { MarkdownNoteEditorHandle } from "./MarkdownNoteEditor";
import { useCanvasInteractionSettings } from "../canvas/CanvasInteractionContext";

const inputClass = "nodrag nopan nowheel node-input";
const MarkdownNoteEditor = lazy(() => import("./MarkdownNoteEditor"));
const NOTE_AUTO_HEIGHT_MIN = 84;
const NOTE_AUTO_HEIGHT_MAX = 1200;
const NOTE_AUTO_HEIGHT_DRAG_SPACE = 30;

export const NoteNode = memo(function NoteNode({ id, data, selected, update }: NodeRendererProps) {
  const editorRef = useRef<MarkdownNoteEditorHandle>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const onNodesChange = useEditorStore((state) => state.onNodesChange);
  const { noteSelectionTooltipEnabled } = useCanvasInteractionSettings();
  const [isEditing, setIsEditing] = useState(false);
  const autoHeight = data.autoHeight !== false;
  useEffect(() => {
    if (!selected) setIsEditing(false);
  }, [selected]);
  useEffect(() => {
    if (!autoHeight) return;
    const surface = surfaceRef.current;
    if (!surface) return;

    let animationFrame = 0;
    let settleTimer = 0;
    let measuredContent: HTMLElement | null = null;
    let pendingDimensions: { width: number; height: number } | null = null;
    const applyDimensions = (dimensions: { width: number; height: number }, resizing: boolean) => {
      onNodesChange([{
        id,
        type: "dimensions",
        dimensions,
        resizing,
        setAttributes: true,
      }]);
    };
    const scheduleMeasure = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        if (!measuredContent) return;
        const desiredHeight = Math.max(
          NOTE_AUTO_HEIGHT_MIN,
          Math.min(NOTE_AUTO_HEIGHT_MAX, Math.ceil(measuredContent.scrollHeight + NOTE_AUTO_HEIGHT_DRAG_SPACE)),
        );
        const node = useEditorStore.getState().nodes.find((candidate) => candidate.id === id);
        if (!node) return;
        const currentHeight = node.measured?.height ?? node.height ?? NOTE_AUTO_HEIGHT_MIN;
        if (Math.abs(currentHeight - desiredHeight) < 1) return;
        const width = node.measured?.width ?? node.width ?? 240;
        pendingDimensions = { width, height: desiredHeight };
        applyDimensions(pendingDimensions, true);
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(() => {
          if (!pendingDimensions) return;
          applyDimensions(pendingDimensions, false);
          pendingDimensions = null;
        }, 220);
      });
    };
    const resizeObserver = new ResizeObserver(scheduleMeasure);
    const observeCurrentContent = () => {
      const nextContent = surface.querySelector<HTMLElement>(
        isEditing ? ".kimnote-markdown-content" : ".markdown-preview",
      );
      if (nextContent !== measuredContent) {
        resizeObserver.disconnect();
        measuredContent = nextContent;
        if (measuredContent) resizeObserver.observe(measuredContent);
      }
      scheduleMeasure();
    };
    const mutationObserver = new MutationObserver(observeCurrentContent);
    mutationObserver.observe(surface, { childList: true, subtree: true, characterData: true });
    observeCurrentContent();

    return () => {
      cancelAnimationFrame(animationFrame);
      window.clearTimeout(settleTimer);
      if (pendingDimensions) applyDimensions(pendingDimensions, false);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [autoHeight, id, isEditing, onNodesChange]);
  const updateMarkdown = (content: string) =>
    update({ content, updatedAt: new Date().toISOString() }, "编辑 Markdown 便签");
  return (
    <NodeFrame color={data.color} selected={selected} className="note-node">
      <div
        ref={surfaceRef}
        className="note-node__surface"
        data-auto-height={autoHeight}
        title="单击文字编辑；空白区域可拖动"
        onClick={(event) => {
          const target = event.target;
          if (!(target instanceof Element) || !target.closest(
            ".markdown-preview :is(p, h1, h2, h3, h4, h5, h6, li, blockquote, pre, th, td, .markdown-preview__placeholder)",
          )) return;
          setIsEditing(true);
        }}
        onDoubleClick={(event) => {
          event.stopPropagation();
          setIsEditing(true);
          editorRef.current?.focus();
        }}
      >
        {selected && isEditing ? (
          <Suspense fallback={<MarkdownPreview markdown={data.content ?? ""} />}>
            <MarkdownNoteEditor
              ref={editorRef}
              markdown={data.content ?? ""}
              onChange={updateMarkdown}
              autoFocus
              selectionTooltipEnabled={noteSelectionTooltipEnabled}
            />
          </Suspense>
        ) : <MarkdownPreview markdown={data.content ?? ""} />}
      </div>
    </NodeFrame>
  );
});

export const NoteDocumentWindow = memo(function NoteDocumentWindow({ data, update }: NodeRendererProps) {
  return (
    <Suspense fallback={<MarkdownPreview markdown={data.content ?? ""} compact={false} />}>
      <MarkdownNoteEditor
        markdown={data.content ?? ""}
        documentTitle={data.title || "便签"}
        onChange={(content) => update(
          { content, updatedAt: new Date().toISOString() },
          "编辑 Markdown 便签",
        )}
        mode="document"
        autoFocus
      />
    </Suspense>
  );
});

export const TextNode = memo(function TextNode({ data, selected, update }: NodeRendererProps) {
  return (
    <NodeFrame color={data.color} selected={selected} bare>
      <textarea
        className={`${inputClass} text-editor`}
        aria-label="文本内容"
        placeholder="开始输入文本…"
        value={data.content ?? ""}
        onChange={(event) => update({ content: event.target.value, updatedAt: new Date().toISOString() })}
      />
    </NodeFrame>
  );
});

export const HeadingNode = memo(function HeadingNode({ data, selected, update }: NodeRendererProps) {
  return (
    <NodeFrame color={data.color} selected={selected} bare>
      <textarea
        className={`${inputClass} heading-editor`}
        aria-label="标题"
        rows={2}
        placeholder="新标题"
        value={data.content ?? ""}
        onChange={(event) => update({ content: event.target.value, updatedAt: new Date().toISOString() })}
      />
    </NodeFrame>
  );
});

export const ChecklistNode = memo(function ChecklistNode({ data, selected, update }: NodeRendererProps) {
  const items = data.items ?? [];
  const completeCount = items.filter((item) => item.checked).length;

  const patchItems = (nextItems: ChecklistItem[]) =>
    update({ items: nextItems, updatedAt: new Date().toISOString() }, "编辑清单");

  const addItem = () =>
    patchItems([...items, { id: crypto.randomUUID(), text: "", checked: false }]);

  return (
    <NodeFrame
      color={data.color}
      selected={selected}
      header={
        <div className="node-title-row">
          <input
            className={`${inputClass} node-title-input`}
            value={data.title}
            aria-label="清单标题"
            onChange={(event) => update({ title: event.target.value, updatedAt: new Date().toISOString() })}
          />
          <span className="checklist-count">{completeCount}/{items.length}</span>
        </div>
      }
    >
      <div className="checklist-items">
        {items.map((item) => (
          <div className="checklist-row" key={item.id}>
            <button
              type="button"
              className={`nodrag nopan checklist-toggle ${item.checked ? "is-done" : ""}`}
              aria-label={item.checked ? "标记为未完成" : "标记为完成"}
              onClick={() =>
                patchItems(items.map((current) => current.id === item.id ? { ...current, checked: !current.checked } : current))
              }
            >
              {item.checked ? <Check size={12} strokeWidth={3} /> : null}
            </button>
            <input
              className={`${inputClass} checklist-input ${item.checked ? "is-done" : ""}`}
              value={item.text}
              placeholder="待办事项"
              onChange={(event) =>
                patchItems(items.map((current) => current.id === item.id ? { ...current, text: event.target.value } : current))
              }
            />
            <button
              type="button"
              className="nodrag nopan checklist-delete"
              aria-label="删除事项"
              onClick={() => patchItems(items.filter((current) => current.id !== item.id))}
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="nodrag nopan add-checklist-item" onClick={addItem}>
        <Plus size={13} /> 添加事项
      </button>
    </NodeFrame>
  );
});

function normalizeUrl(value?: string): string | undefined {
  if (!value?.trim()) return;
  try {
    const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    const url = new URL(withProtocol);
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : undefined;
  } catch {
    return;
  }
}

export const LinkNode = memo(function LinkNode({ data, selected, update }: NodeRendererProps) {
  const href = useMemo(() => normalizeUrl(data.url), [data.url]);
  const host = href ? new URL(href).hostname.replace(/^www\./, "") : "粘贴网页链接";
  return (
    <NodeFrame color={data.color} selected={selected} header={<span className="node-kind-label"><Link2 size={13} /> 链接</span>}>
      <input
        className={`${inputClass} link-title-input`}
        aria-label="链接标题"
        placeholder="链接标题"
        value={data.title}
        onChange={(event) => update({ title: event.target.value, updatedAt: new Date().toISOString() })}
      />
      <input
        className={`${inputClass} link-url-input`}
        aria-label="网址"
        placeholder="https://"
        value={data.url ?? ""}
        onChange={(event) => update({ url: event.target.value, updatedAt: new Date().toISOString() })}
      />
      <div className="link-node__footer">
        <span>{host}</span>
        <button
          type="button"
          className="nodrag nopan link-open"
          disabled={!href}
          onClick={() => href && window.open(href, "_blank", "noopener,noreferrer")}
          aria-label="打开链接"
        >
          <ExternalLink size={14} />
        </button>
      </div>
    </NodeFrame>
  );
});

export const ImageNode = memo(function ImageNode({ data, selected, update }: NodeRendererProps) {
  const src = useAssetUrl(data.assetHash);
  return (
    <NodeFrame color={data.color} selected={selected} className="image-node" bare>
      {src ? (
        <img className="image-node__media" src={src} alt={data.title || data.fileName || "画布图片"} draggable={false} />
      ) : (
        <div className="image-node__empty"><ImageOff size={24} /><span>图片不可用</span></div>
      )}
      <input
        className={`${inputClass} image-caption`}
        aria-label="图片说明"
        placeholder="添加说明…"
        value={data.title}
        onChange={(event) => update({ title: event.target.value, updatedAt: new Date().toISOString() })}
      />
    </NodeFrame>
  );
});

export const FileNode = memo(function FileNode({ data, selected }: NodeRendererProps) {
  const src = useAssetUrl(data.assetHash);
  const size = data.fileSize ? formatBytes(data.fileSize) : "本地附件";
  return (
    <NodeFrame color={data.color} selected={selected} header={<span className="node-kind-label"><File size={13} /> 文件</span>}>
      <div className="file-node__body">
        <div className="file-node__icon"><File size={22} /></div>
        <div className="file-node__meta">
          <strong title={data.fileName}>{data.fileName || data.title || "未命名文件"}</strong>
          <span>{size}</span>
        </div>
        <a
          className={`nodrag nopan file-download ${src ? "" : "is-disabled"}`}
          href={src}
          download={data.fileName || "attachment"}
          aria-label="下载文件"
        >
          <Download size={15} />
        </a>
      </div>
    </NodeFrame>
  );
});

export const ColumnNode = memo(function ColumnNode({ data, selected, update }: NodeRendererProps) {
  return (
    <NodeFrame color={data.color} selected={selected} className="column-node" header={
      <input
        className={`${inputClass} node-title-input`}
        aria-label="分区标题"
        value={data.title}
        onChange={(event) => update({ title: event.target.value, updatedAt: new Date().toISOString() })}
      />
    }>
      <textarea
        className={`${inputClass} column-editor`}
        aria-label="分区说明"
        placeholder="在这里整理一个主题…"
        value={data.content ?? ""}
        onChange={(event) => update({ content: event.target.value, updatedAt: new Date().toISOString() })}
      />
    </NodeFrame>
  );
});

export const CommentNode = memo(function CommentNode({ data, selected, update }: NodeRendererProps) {
  return (
    <NodeFrame color={data.color ?? "rose"} selected={selected} header={
      <span className="node-kind-label"><MessageCircle size={13} /> 评论 · 本地</span>
    }>
      <textarea
        className={`${inputClass} comment-editor`}
        aria-label="评论内容"
        placeholder="添加一条评论…"
        value={data.content ?? ""}
        onChange={(event) => update({ content: event.target.value, updatedAt: new Date().toISOString() })}
      />
    </NodeFrame>
  );
});

function normalizeTable(value: unknown): string[][] {
  if (!Array.isArray(value)) return [["", "", ""], ["", "", ""], ["", "", ""]];
  return value.map((row) => Array.isArray(row) ? row.map((cell) => String(cell ?? "")) : []);
}

export const TableNode = memo(function TableNode({ data, selected, update }: NodeRendererProps) {
  const table = normalizeTable(data.table);
  const columnCount = Math.max(1, ...table.map((row) => row.length));
  const patchTable = (next: string[][]) => update({ table: next, updatedAt: new Date().toISOString() }, "编辑表格");

  return (
    <NodeFrame color={data.color} selected={selected} className="table-node" header={
      <div className="node-title-row">
        <Grid3X3 size={13} />
        <input
          className={`${inputClass} node-title-input`}
          aria-label="表格标题"
          value={data.title}
          onChange={(event) => update({ title: event.target.value, updatedAt: new Date().toISOString() })}
        />
      </div>
    }>
      <div className="table-node__scroll nodrag nowheel">
        <div className="table-node__grid" style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(82px, 1fr))` }}>
          {table.flatMap((row, rowIndex) =>
            Array.from({ length: columnCount }, (_, columnIndex) => (
              <input
                key={`${rowIndex}:${columnIndex}`}
                className="nopan node-input table-cell"
                aria-label={`第 ${rowIndex + 1} 行第 ${columnIndex + 1} 列`}
                value={row[columnIndex] ?? ""}
                onChange={(event) => {
                  const next = table.map((current) => [...current]);
                  while (next[rowIndex].length < columnCount) next[rowIndex].push("");
                  next[rowIndex][columnIndex] = event.target.value;
                  patchTable(next);
                }}
              />
            )),
          )}
        </div>
      </div>
      <div className="table-node__actions">
        <button type="button" className="nodrag nopan" onClick={() => patchTable([...table, Array(columnCount).fill("")])}><Plus size={12} /> 行</button>
        <button type="button" className="nodrag nopan" onClick={() => patchTable(table.map((row) => [...row, ""]))}><Plus size={12} /> 列</button>
      </div>
    </NodeFrame>
  );
});

const sketchColors = ["#252521", "#3975de", "#d25a55", "#3d8b62", "#a46abb"];

export const SketchNode = memo(function SketchNode({ data, selected, update }: NodeRendererProps) {
  const strokes = data.strokes ?? [];
  const [draft, setDraft] = useState<SketchPoint[]>([]);
  const [color, setColor] = useState(sketchColors[0]);
  const drawing = useRef(false);
  const draftRef = useRef<SketchPoint[]>([]);
  const drawFrame = useRef<number | null>(null);

  const localPoint = (clientX: number, clientY: number, bounds: DOMRect): SketchPoint => {
    return {
      x: ((clientX - bounds.left) / bounds.width) * 320,
      y: ((clientY - bounds.top) / bounds.height) * 210,
    };
  };

  const finishStroke = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!drawing.current) return;
    drawing.current = false;
    event.currentTarget.releasePointerCapture(event.pointerId);
    if (drawFrame.current) cancelAnimationFrame(drawFrame.current);
    drawFrame.current = null;
    const points = draftRef.current;
    if (points.length > 1) {
      const stroke: SketchStroke = { id: crypto.randomUUID(), color, width: 2.4, points };
      update({ strokes: [...strokes, stroke], updatedAt: new Date().toISOString() }, "绘制笔迹");
    }
    draftRef.current = [];
    setDraft([]);
  };

  return (
    <NodeFrame color={data.color} selected={selected} className="sketch-node" header={
      <div className="node-title-row">
        <Palette size={13} />
        <input className={`${inputClass} node-title-input`} aria-label="画板标题" value={data.title} onChange={(event) => update({ title: event.target.value })} />
        <div className="sketch-palette nodrag nopan">
          {sketchColors.map((option) => <button key={option} type="button" aria-label={`画笔 ${option}`} className={option === color ? "is-active" : ""} style={{ background: option }} onClick={() => setColor(option)} />)}
          <button type="button" className="sketch-clear" aria-label="清空笔迹" onClick={() => update({ strokes: [] }, "清空笔迹")}><Trash2 size={11} /></button>
        </div>
      </div>
    }>
      <svg
        className="nodrag nopan sketch-surface"
        viewBox="0 0 320 210"
        preserveAspectRatio="none"
        aria-label="手绘区域"
        onPointerDown={(event) => {
          drawing.current = true;
          event.currentTarget.setPointerCapture(event.pointerId);
          const point = localPoint(event.clientX, event.clientY, event.currentTarget.getBoundingClientRect());
          draftRef.current = [point];
          setDraft([point]);
        }}
        onPointerMove={(event) => {
          if (!drawing.current) return;
          const bounds = event.currentTarget.getBoundingClientRect();
          const events = event.nativeEvent.getCoalescedEvents?.() ?? [event.nativeEvent];
          for (const pointer of events) {
            draftRef.current.push(localPoint(pointer.clientX, pointer.clientY, bounds));
          }
          if (!drawFrame.current) {
            drawFrame.current = requestAnimationFrame(() => {
              drawFrame.current = null;
              setDraft([...draftRef.current]);
            });
          }
        }}
        onPointerUp={finishStroke}
        onPointerCancel={finishStroke}
      >
        {strokes.map((stroke) => <path key={stroke.id} d={strokePath(stroke.points)} fill="none" stroke={stroke.color} strokeWidth={stroke.width} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />)}
        {draft.length > 1 ? <path d={strokePath(draft)} fill="none" stroke={color} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" /> : null}
      </svg>
    </NodeFrame>
  );
});

function strokePath(points: SketchPoint[]): string {
  if (!points.length) return "";
  return points.reduce((path, point, index) => `${path}${index ? " L" : "M"}${point.x.toFixed(1)},${point.y.toFixed(1)}`, "");
}

export const BoardPortalNode = memo(function BoardPortalNode({ data, selected, update }: NodeRendererProps) {
  const boards = useEditorStore((state) => state.boards);
  const currentBoard = useEditorStore((state) => state.board);
  const selectBoard = useEditorStore((state) => state.selectBoard);
  const target = boards.find((board) => board.id === data.targetBoardId);
  const available = boards.filter((board) => board.id !== currentBoard?.id);

  return (
    <NodeFrame color={data.color ?? "lavender"} selected={selected} className="board-portal-node" header={<span className="node-kind-label"><Grid3X3 size={13} /> 画板入口</span>}>
      <input className={`${inputClass} board-portal-title`} aria-label="入口标题" value={data.title} onChange={(event) => update({ title: event.target.value })} />
      {target ? (
        <>
          <button type="button" className="nodrag nopan board-portal-open" onClick={() => void selectBoard(target.id)}>
            打开「{target.title}」
          </button>
          <span className="board-portal-meta" title={target.id}>Board · {target.id.slice(-8)}</span>
        </>
      ) : (
        <p className="board-portal-hint">正在创建独立画板…</p>
      )}
      <select
        className="nodrag nopan nowheel board-portal-select"
        aria-label="引用画板"
        value={target?.id ?? ""}
        onChange={(event) => update({ targetBoardId: event.target.value }, "更改画板引用")}
      >
        <option value="" disabled>选择一个画板…</option>
        {available.map((board) => <option value={board.id} key={board.id}>{board.title}</option>)}
      </select>
    </NodeFrame>
  );
});

export const UnknownNode = memo(function UnknownNode({ data, selected }: NodeRendererProps) {
  return (
    <NodeFrame color="graphite" selected={selected} header={<span>未知组件 · {data.kind}</span>}>
      <p className="unknown-node-copy">组件尚未安装。原始数据已安全保留，安装对应扩展后即可恢复。</p>
    </NodeFrame>
  );
});

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}
